import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  FaBook,
  FaBuilding,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaMoneyBillWave,
} from "react-icons/fa";
import { formatIntakeLabel, formatTuitionFee } from "./counselingFilterUtils";

const PLACEHOLDER = "—";

function resolveProviderId(course) {
  const id =
    course?.providerid ??
    course?.provider_id ??
    course?.variant_providers?.[0]?.id ??
    null;
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function buildProviderDetailPath(providerId) {
  if (!providerId) return null;
  return `/providers/provider-details/${providerId}/`;
}

function getLevelBadgeClass(level) {
  const value = String(level || "").toLowerCase();
  if (value.includes("master") || value.includes("postgraduate")) {
    return "bg-violet-100 text-violet-700 ring-violet-200/80";
  }
  if (value.includes("bachelor") || value.includes("undergraduate")) {
    return "bg-blue-100 text-blue-700 ring-blue-200/80";
  }
  if (value.includes("diploma") || value.includes("advanced")) {
    return "bg-amber-100 text-amber-800 ring-amber-200/80";
  }
  if (value.includes("certificate")) {
    return "bg-teal-100 text-teal-700 ring-teal-200/80";
  }
  return "bg-indigo-100 text-indigo-700 ring-indigo-200/80";
}

function MetaRow({ icon, label, value, iconClass, labelClass, valueClass }) {
  const isEmpty = !value || value === PLACEHOLDER;

  return (
    <div className="flex min-h-[18px] items-start gap-1.5">
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${iconClass}`}
      >
        {icon}
      </span>
      <p className="m-0 min-w-0 flex-1 leading-snug">
        <span className={`text-[9px] font-bold uppercase tracking-wide ${labelClass}`}>
          {label}
        </span>
        <span
          className={`block truncate text-[11px] font-medium ${
            isEmpty ? "text-slate-300" : valueClass
          }`}
        >
          {value || PLACEHOLDER}
        </span>
      </p>
    </div>
  );
}

function CounselingCourseCard({ course }) {
  const intakeLabel = formatIntakeLabel(course) || course.intake || PLACEHOLDER;
  const tuition = formatTuitionFee(course) || PLACEHOLDER;
  const providerId = resolveProviderId(course);
  const detailHref = buildProviderDetailPath(providerId);
  const hasTuition = tuition !== PLACEHOLDER;

  return (
    <article className="counseling-course-card group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#007BFF]/30 hover:shadow-md">
      <div className="h-1 shrink-0 bg-gradient-to-r from-[#007BFF] via-sky-400 to-indigo-400" />

      <div className="border-b border-slate-100 px-3.5 py-3">
        <div className="mb-2 flex min-h-[20px] items-center justify-between gap-2">
          {course.level ? (
            <span
              className={`inline-flex max-w-[70%] items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ring-1 ${getLevelBadgeClass(
                course.level
              )}`}
            >
              <span className="truncate">{course.level}</span>
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-300 ring-1 ring-slate-200">
              No level
            </span>
          )}
          {course.discipline ? (
            <span className="inline-flex max-w-[45%] shrink-0 items-center rounded-full bg-fuchsia-50 px-2 py-0.5 text-[9px] font-semibold text-fuchsia-700 ring-1 ring-fuchsia-200/80">
              <FaBook size={7} className="mr-1 shrink-0" />
              <span className="truncate">{course.discipline}</span>
            </span>
          ) : null}
        </div>

        <h3
          className="m-0 line-clamp-2 min-h-[34px] text-[13px] font-bold leading-snug text-slate-900"
          title={course.name}
        >
          {course.name || PLACEHOLDER}
        </h3>

        <p className="m-0 mt-1.5 flex min-h-[16px] items-center gap-1.5 text-[11px]">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-sky-50 text-sky-600">
            <FaBuilding size={9} />
          </span>
          <span
            className={`truncate font-medium ${
              course.providername ? "text-slate-700" : "text-slate-300"
            }`}
          >
            {course.providername || PLACEHOLDER}
          </span>
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 px-3.5 py-3">
        <div className="min-h-[24px]">
          <span
            className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
              course.country
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200/80"
                : "bg-slate-50 text-slate-300 ring-slate-200"
            }`}
          >
            <FaMapMarkerAlt size={8} className="shrink-0" />
            <span className="truncate">{course.country || PLACEHOLDER}</span>
          </span>
        </div>

        <div
          className={`rounded-lg px-2.5 py-2 ring-1 ${
            hasTuition
              ? "bg-gradient-to-br from-emerald-50 to-teal-50 ring-emerald-200/70"
              : "bg-slate-50 ring-slate-200"
          }`}
        >
          <p className="m-0 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700/80">
            <FaMoneyBillWave size={9} />
            Tuition fee
          </p>
          <p
            className={`m-0 mt-0.5 min-h-[20px] text-sm font-bold leading-tight ${
              hasTuition ? "text-emerald-800" : "text-slate-300"
            }`}
          >
            {tuition}
          </p>
        </div>

        <div className="min-h-[72px] space-y-2">
          <MetaRow
            icon={<FaClock size={8} />}
            label="Duration"
            value={course.duration}
            iconClass="bg-sky-50 text-sky-600"
            labelClass="text-sky-600/80"
            valueClass="text-slate-700"
          />
          <MetaRow
            icon={<FaCalendarAlt size={8} />}
            label="Intake"
            value={intakeLabel}
            iconClass="bg-orange-50 text-orange-600"
            labelClass="text-orange-600/80"
            valueClass="text-slate-700"
          />
          {!course.discipline ? (
            <MetaRow
              icon={<FaBook size={8} />}
              label="Discipline"
              value={course.discipline}
              iconClass="bg-fuchsia-50 text-fuchsia-600"
              labelClass="text-fuchsia-600/80"
              valueClass="text-slate-700"
            />
          ) : (
            <div className="min-h-[18px]" aria-hidden="true" />
          )}
        </div>
      </div>

      <div className="mt-auto border-t border-slate-100 px-3.5 py-2.5">
        {detailHref ? (
          <Link
            to={detailHref}
            className="inline-flex min-h-[28px] w-full items-center justify-center rounded-lg bg-[#007BFF]/10 px-3 text-[11px] font-semibold text-[#007BFF] no-underline transition hover:bg-[#007BFF] hover:text-white"
          >
            View provider
          </Link>
        ) : (
          <span
            className="inline-flex min-h-[28px] w-full items-center justify-center rounded-lg bg-slate-100 px-3 text-[11px] font-semibold text-slate-400"
            aria-disabled="true"
          >
            Provider unavailable
          </span>
        )}
      </div>
    </article>
  );
}

export function CounselingCourseCardSkeleton() {
  return (
    <div className="counseling-course-card counseling-course-card--skeleton animate-pulse overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="h-1 bg-slate-200" />
      <div className="space-y-2.5 border-b border-slate-100 px-3.5 py-3">
        <div className="flex gap-2">
          <div className="h-5 w-16 rounded-full bg-slate-200" />
          <div className="h-5 w-14 rounded-full bg-slate-100" />
        </div>
        <div className="h-3.5 w-full rounded bg-slate-200" />
        <div className="h-2.5 w-2/3 rounded bg-slate-100" />
      </div>
      <div className="space-y-2.5 px-3.5 py-3">
        <div className="h-5 w-1/2 rounded-full bg-slate-100" />
        <div className="h-12 w-full rounded-lg bg-slate-100" />
        <div className="h-2 w-full rounded bg-slate-100" />
        <div className="h-2 w-4/5 rounded bg-slate-100" />
      </div>
      <div className="border-t border-slate-100 px-3.5 py-2.5">
        <div className="h-7 w-full rounded-lg bg-slate-100" />
      </div>
    </div>
  );
}

export default memo(CounselingCourseCard);
